import React from 'react';
import cn from 'clsx';
import { Layout } from 'fronton-react';
import { FilePdfIcon } from '@fronton/icons-react';
import {
  BenefitsCard,
  ButtonWithGAClickButton,
  CalculatorPanel,
  CreditBlock,
  HeaderSection,
  LinkWithGAClickButton,
  OtherSection,
  ReviewsBenefits,
  ReviewsCatalog,
  ReviewsSection,
  SectionSubTitle,
  SignSection,
  Typography,
} from 'common/components';
import { fullWidthMobileButton } from 'common/styles/common';
import { SpecialOffer } from 'remotes/special-offer';
import { PromoBlock } from 'remotes/promo-block';
import { BATHROOM_CDN_PATH } from '../old-bathroom/old-bathroom-constants';
import {
  BENEFITS_CARD_LIST,
  NewBathroomCollections,
  NewBathroomDesignPanel,
  NewBathroomGallery,
  NewBathroomObjects,
  NewBathroomQuestions,
  NewBathroomRepair,
} from './components';
import { BATHROOM_ELEMENT_BLOCK, BATHROOM_LANDING_NAME } from '../bathroom-constants';
import {
  BATHROOM_CALCULATOR_HREF,
  BATHROOM_REVIEWS_BENEFITS_LIST,
  BATHROOM_REVIEWS_LIST,
  BATHROOM_SIGN_SECTION_GALLERY_LIST,
} from './new-bathroom-constants';
import {
  bathroomBenefitsReview,
  bathroomBenefitsReviewButton,
  bathroomReviewsLink,
  signSection,
  signTitle,
  signTitleDesktop,
} from './new-bathroom-styles';

type NewBathroomProps = {
  designRegistrationHref: string;
  regionId: string;
};

/**
 * Вью страницы "Ванные комнаты"
 */
export const NewBathroom: React.FC<NewBathroomProps> = ({ designRegistrationHref, regionId }) => (
  <>
    <HeaderSection
      bgColor="#51423C"
      description={`Реализуем любой проект\u00A0\u2014 от\u00A0лёгкого обновления до\u00A0полного преображения`}
      img={{
        ldSrcSet: `${BATHROOM_CDN_PATH}/header/header-new-ld.jpg/header-new-ld-02.jpg`,
        dSrcSet: `${BATHROOM_CDN_PATH}/header/header-new-d.jpg/header-new-d-02.jpg`,
        tSrcSet: `${BATHROOM_CDN_PATH}/header/header-new-t.jpg/header-new-t-02.jpg`,
        mSrcSet: `${BATHROOM_CDN_PATH}/header/header-new-m.jpg/header-new-m-04.jpg`,
        title: 'Изображение ванной комнаты',
        alt: 'Изображение ванной комнаты',
      }}
      mediaType="image"
      title="ДИЗАЙН И РЕМОНТ ВАННЫХ КОМНАТ"
    />
    <NewBathroomDesignPanel designRegistrationHref={designRegistrationHref} />
    <Layout>
      <BenefitsCard
        cardList={BENEFITS_CARD_LIST}
        elementBlock={BATHROOM_ELEMENT_BLOCK}
        elementName="Дизайн проект ванной комнаты"
        renderModalContent={({ modal: ModalContent }) => ModalContent && <ModalContent />}
      />
      <PromoBlock elementBlock={BATHROOM_ELEMENT_BLOCK} />
      <SpecialOffer elementBlock={BATHROOM_ELEMENT_BLOCK} />
      <NewBathroomGallery />
    </Layout>
    <SignSection
      className={signSection}
      createProjectTitle={'Создать проект с\u00A0персональным менеджером можно онлайн'}
      elementBlock={BATHROOM_ELEMENT_BLOCK}
      elementName="Приезжайте в гости — посмотрите комплекты для ванных комнат вживую"
      galleryList={BATHROOM_SIGN_SECTION_GALLERY_LIST}
      signHref={designRegistrationHref}
      renderTitle={(titleClassName) => (
        <>
          <SectionSubTitle className={cn(signTitle, titleClassName)} margin="s" color="text-invert">
            Приезжайте в&nbsp;гости&nbsp;&mdash; посмотрите комплекты для ванных комнат вживую
          </SectionSubTitle>
          <SectionSubTitle className={cn(signTitleDesktop, titleClassName)} margin="s" color="text-invert">
            Приезжайте в&nbsp;гости&nbsp;&mdash; посмотрите комплекты для ванных комнат
          </SectionSubTitle>
        </>
      )}
    />
    <CalculatorPanel
      calculatoHref={BATHROOM_CALCULATOR_HREF}
      description="Узнайте примерную стоимость ремонта в онлайн-калькуляторе"
      elementBlock={BATHROOM_ELEMENT_BLOCK}
      elementName="Рассчитать стоимость ремонта"
      title="Рассчитать стоимость ремонта"
    />
    <Layout>
      <CreditBlock />
      <NewBathroomObjects />
    </Layout>
    <ReviewsSection
      benefits={
        <>
          <ReviewsBenefits benefitsList={BATHROOM_REVIEWS_BENEFITS_LIST} className={bathroomBenefitsReview} />
          <ButtonWithGAClickButton
            className={bathroomBenefitsReviewButton}
            elementBlock={BATHROOM_ELEMENT_BLOCK}
            elementName="Что входит в бесплатный дизайн-проект"
            elementText="Скачать пример проекта"
            href="https://storage.yandexcloud.net/lemanapro-ru-landings-docs/bathroom/bathroom-project-example.pdf"
            iconLeft={<FilePdfIcon />}
            invert
            rel="noreferrer"
            title="Пример проектов ванной комнаты"
            target="_blank"
          >
            Скачать пример проекта
          </ButtonWithGAClickButton>
        </>
      }
      review={
        <ReviewsCatalog
          elementBlock={BATHROOM_ELEMENT_BLOCK}
          modalButton={
            <ButtonWithGAClickButton
              className={fullWidthMobileButton}
              elementBlock={`${BATHROOM_ELEMENT_BLOCK} / Reviews`}
              elementName="Отзывы клиентов"
              elementText="Записаться на проектирование"
              title="Запишитесь на проектирование"
              href={designRegistrationHref}
              target="_blank"
              variant="accent"
              wide
            >
              Записаться на проектирование
            </ButtonWithGAClickButton>
          }
          newVariant
          reviewsList={BATHROOM_REVIEWS_LIST}
        />
      }
      description="Делаем ванные комнаты, которые хочется показывать"
      title={<>
        <LinkWithGAClickButton
          className={bathroomReviewsLink}
          elementBlock={`${BATHROOM_ELEMENT_BLOCK} / Reviews`}
          elementName="Отзывы клиентов"
          elementText="Запишитесь"
          title="Запишитесь на проектирование"
          href={designRegistrationHref}
          target="_blank">
          <Typography variant="h2" color="text-invert">
            Запишитесь
          </Typography>
        </LinkWithGAClickButton>{' '}
        на&nbsp;бесплатное проектирование, <span>а&nbsp;мы&nbsp;позаботимся о&nbsp;всех этапах</span>
      </>
      }
    />
    <Layout>
      <NewBathroomCollections
        elementBlock={BATHROOM_ELEMENT_BLOCK}
      />
      <NewBathroomRepair regionId={regionId} />
      <NewBathroomQuestions designSignHref={designRegistrationHref} />
    </Layout>
    <OtherSection
      elementBlock="landingVannuyuKomnatu / other"
      landingName={BATHROOM_LANDING_NAME}
      promotionName="vannuyuKomnatu_bottom"
    />
  </>
);
